import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { allHeroSlides } from "@/lib/video";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const total = allHeroSlides.length;

  const goTo = useCallback(
    (index: number) => {
      setActive((index + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  return (
    <section className="relative h-[420px] w-full overflow-hidden rounded-lg bg-foreground-950 md:h-[520px]">
      {allHeroSlides.map((slide, index) => (
        <div
          key={slide.videoId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 逼特逼视频`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
        </div>
      ))}

      <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
        <div className="max-w-2xl">
          {allHeroSlides.map((slide, index) =>
            index === active ? (
              <div key={slide.videoId} className="animate-fade-up">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-primary-950">
                  <i className="ri-fire-fill" />
                  {slide.label}
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl">
                  {slide.title}
                </h2>
                <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {slide.subtitle}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${slide.videoId}`}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-lg" />
                    立即观看
                  </Link>
                  <Link
                    to="/search"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                  >
                    探索更多内容
                  </Link>
                </div>
              </div>
            ) : null,
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(active - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(active + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-6 flex items-center gap-2 md:left-12">
        {allHeroSlides.map((slide, index) => (
          <button
            key={slide.videoId}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}